import {
  DropdownIndicator,
  formatGroupLabel,
  formatOptionLabel,
  IndicatorSeparator,
  getTranslatedText,
  DEFAULT_FIELDS_TRANSLATED_LABEL
} from '@nile/components';
import {
  apiResolver,
  getApiMethodAndKeyNames,
  SystrayAccountClient
} from '@nile/graphql';
import React, { useState } from 'react';
import Select from 'react-select';
import styled from 'styled-components';
import { injectIntl } from 'react-intl';
import {
  reactSelectErrorStyles,
  reactSelectInlineError
} from '@nile/banyan/src/Utils/style';
import { Dropdown } from '@superops/ui-design-system';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEmpty } from 'lodash';

const EmptyDiv = styled.div``;

const Wrapper = styled.div`
  width: 100%;
  padding-left: 2px;
  .value_selector__control {
    border: none;
    border-bottom: 1px solid #ececec;
  }
  .value_selector__single-value {
    font-weight: bold;
  }
`;

const Label = styled.div`
  color: #616263;
  font-weight: 400 !important;
  text-align: left;
  margin-top: 5px;
`;

function AsyncPropSelector(props) {
  const {
    intl,
    field,
    defaultValue,
    parentValue,
    isDynamicQuery,
    dynamicQuery,
    generateOptions,
    isMulti,
    entity,
    hideTitle,
    value,
    isClearable,
    isDisabled,
    defaultMenuIsOpen,
    noPortalTarget,
    isDark,
    autoFocus,
    onKeyDown,
    isMandatory,
    error,
    showErrorMessage,
    placeholder,
    customMenuOptions,
    customComponents,
    systray = false,
    dependantFieldEnabled = false,
    isUiDesignSystem
  } = props;

  const flags = useFlags();
  const runbookFlag = flags?.unifiedRunbooks;

  const [loading, setLoading] = useState(false);
  const [values, setValues] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);

  const resolverModule = entity || 'Ticket';
  const getDisplayString = (key, element) => {
    if (typeof element === 'string') {
      return element;
    }
    if (typeof key === 'object') {
      return `${element[key[0]]} ${element[key[1]]}`; //todo vignesh loop
    }
    return element[key];
  };
  const selectComponents = {
    IndicatorSeparator,
    DropdownIndicator: isDark
      ? (props) => <DropdownIndicator {...props} color="#ffffff" />
      : (props) => <DropdownIndicator {...props} />
  };
  if (customMenuOptions) {
    selectComponents.Menu = customMenuOptions;
  }
  if (customComponents) {
    Object.assign(selectComponents, customComponents);
  }
  const fetchValues = async (input, cb) => {
    if (!isDynamicQuery) {
      let fieldName = field?.name;
      if (
        (field?.name === 'sop' || field?.name === 'ticket.sop') &&
        runbookFlag
      ) {
        fieldName = 'RUNBOOK';
      }
      setLoading(true);
      const client = systray
        ? SystrayAccountClient
        : apiResolver[resolverModule][fieldName].client;
      const query = apiResolver[resolverModule][fieldName].api;
      let variables = !isEmpty(props?.variables)
        ? props?.variables
        : apiResolver?.[resolverModule][fieldName]?.variables || {};
      if (field.name === 'workItem.client') {
        variables = {
          listInfo: {
            sort: {
              attribute: 'name',
              order: 'ASC'
            }
          }
        };
      }

      const res = await client.query({
        query: query,
        variables: variables
      });
      const apiMethodAndKeyNames = getApiMethodAndKeyNames(resolverModule);
      const method = apiMethodAndKeyNames[fieldName]?.method;
      const secondaryMethod = apiMethodAndKeyNames[fieldName]?.secondaryMethod;
      let options = [];

      if (secondaryMethod && res.data) {
        options = res.data[method]?.[secondaryMethod];
      } else {
        options = res.data[method];
      }
      const key = apiMethodAndKeyNames[fieldName]?.key;

      if (options) {
        if (field.nestedTo) {
          const nestedProp = apiMethodAndKeyNames?.[fieldName]?.nestedKey;
          let subValues = [];
          if (!parentValue) {
            setValues([]);
          } else {
            if (flags?.dependentField || (systray && dependantFieldEnabled)) {
              const parentOptions = options?.filter(
                (el) => el?.parentOption?.value === parentValue
              );
              if (parentOptions) {
                subValues = subValues.concat(
                  parentOptions?.map((element) => {
                    return {
                      ...element,
                      name: getDisplayString(key, element),
                      value: getDisplayString(key, element),
                      label: getDisplayString(key, element)
                    };
                  })
                );
                setValues(subValues);
              }
            } else {
              const parentOptions = options?.find(
                (el) => el.value === parentValue || el.name === parentValue
              );
              if (parentOptions) {
                subValues = subValues.concat(
                  parentOptions?.[[nestedProp]]?.map((element) => {
                    return {
                      ...element,
                      name: getDisplayString(key, element),
                      value: getDisplayString(key, element),
                      label: getDisplayString(key, element)
                    };
                  })
                );
                setValues(subValues);
              }
            }
          }
        } else {
          const allValues = options.map((element) => ({
            ...element,
            name: getDisplayString(key, element),
            value: getDisplayString(key, element),
            label: getDisplayString(key, element)
          }));
          setValues(allValues);
        }
      }
      setLoading(false);
    } else {
      setLoading(true);
      const res = dynamicQuery ? await dynamicQuery() : {};
      let allValues = generateOptions(field, res.data);
      setValues(allValues);
      setLoading(false);
    }
    return [];
  };
  const updateSelection = (val) => {
    if (props.updateSelection) {
      props.updateSelection(field, val);
    }
  };

  const SelectComponent = isUiDesignSystem ? Dropdown : Select;
  const WrapperDiv = isUiDesignSystem ? EmptyDiv : Wrapper;
  const classProps = isUiDesignSystem
    ? {}
    : {
        className: 'propSelector',
        classNamePrefix: 'value_selector'
      };

  const getTranslatedLabel = () => {
    if (field?.fieldCategory === 'DEFAULT') {
      const i18nKey = DEFAULT_FIELDS_TRANSLATED_LABEL?.[field?.columnName];
      if (i18nKey) {
        return getTranslatedText(intl, i18nKey);
      }
      return getTranslatedText(intl, field?.label);
    }
    return getTranslatedText(intl, field?.label);
  };

  return (
    <WrapperDiv className={isUiDesignSystem ? '' : 'propSelector'}>
      {!hideTitle && (
        <Label>
          {getTranslatedLabel() || ''}
          {isMandatory && (
            <>
              <span className="mandatory-star">*</span>
            </>
          )}
        </Label>
      )}

      <SelectComponent
        onMenuOpen={async () => {
          await fetchValues();
          setMenuOpen(true);
        }}
        onMenuClose={() => {
          setMenuOpen(false);
        }}
        key={defaultValue}
        formatGroupLabel={formatGroupLabel}
        formatOptionLabel={formatOptionLabel}
        defaultValue={defaultValue}
        components={selectComponents}
        onChange={updateSelection}
        options={values}
        placeholder={getTranslatedText(
          intl,
          placeholder || 'admin.slaPolicy.chooseValue'
        )}
        menuPortalTarget={noPortalTarget ? null : document.body}
        menuPlacement="auto"
        noOptionsMessage={() => {
          return loading ? 'Loading...' : 'No options';
        }}
        onKeyDown={!menuOpen ? onKeyDown : undefined}
        isMulti={isMulti}
        isLoading={loading}
        isClearable={isClearable}
        menuIsOpen={menuOpen}
        isDisabled={isDisabled}
        defaultMenuIsOpen={defaultMenuIsOpen}
        autoFocus={(autoFocus && values?.length) || defaultMenuIsOpen}
        openMenuOnFocus={defaultMenuIsOpen}
        styles={
          error
            ? {
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999
                }),
                ...reactSelectErrorStyles
              }
            : {
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999
                })
              }
        }
        {...classProps}
      />
      {error && showErrorMessage && reactSelectInlineError(error)}
    </WrapperDiv>
  );
}

export default injectIntl(AsyncPropSelector);
